<template>
  <v-container
    v-if="hasOrders"
    class="pa-0"
    fluid
  >
    <v-row no-gutters>
      <v-col cols="12">
        <template v-for="cart in completedDataFiltered">
          <v-container
            :key="`cart-${cart.Timestamp}`"
            class="pa-0"
            fluid
          >
            <v-row dense>
              <!-- CART HEADER -->
              <v-col cols="12">
                <div
                  :class="[`${tableStaffColor(cart.StaffId)} lighten-4`]"
                  class="pa-1 grey--text text--darken-3 text-subtitle-1 font-weight-bold rounded"
                >
                  <v-container
                    class="pa-0"
                    fluid
                  >
                    <v-row
                      align="center"
                      dense
                      justify="center"
                    >
                      <v-col cols="8">
                        <div>{{ $tc('Order.Title', 1) }} #{{ cart.OrderNumber }} -&nbsp;<span class="grey--text text--darken-1">{{ cart.Time }}</span></div>

                        <div
                          v-if="cart.StaffName"
                          class="text-caption"
                        >
                          <div class="grey--text text--darken-3">
                            <strong>{{ $tc('Waiter.Title', 1) }}:</strong> {{ cart.StaffName }}
                          </div>
                        </div>
                      </v-col>

                      <v-col
                        class="text-right pr-3"
                        cols="4"
                      >
                        <template v-if="print">
                          <template v-if="canceled">
                            <print-button
                              :menu-position="'left'"
                              class="ml-1"
                              hide-label
                              icon
                              menu-offset-x
                              small
                              @printer-selected="printer => printTableCanceled(cart, printer)"
                            />
                          </template>

                          <template v-if="!unpaid && !paid && !canceled">
                            <v-badge
                              :value="cartHasPreparationPrintsMissing(cart) && isAppPrintLogVisible"
                              class="pulse-badge"
                              color="red"
                              overlap
                            >
                              <template #badge>
                                <v-icon
                                  size="13"
                                  v-text="'mdi-exclamation-thick'"
                                />
                              </template>

                              <print-preparation-button
                                v-if="printOrderPreparation && posUser?.Settings?.AllUsers?.Permissions?.CanPrintOrderPreparation"
                                :menu-position="'left'"
                                :order-print-groups="cart.PrintGroups || []"
                                :order-prints="cart.Prints || []"
                                class="ml-1"
                                hide-label
                                icon
                                menu-offset-x
                                small
                                @printer-group-selected="printerGroup => printTableOrderPreparation(cart, printerGroup)"
                              />
                            </v-badge>

                            <print-button
                              v-if="printOrder && posUser?.Settings?.AllUsers?.Permissions?.CanPrintOrderDocument"
                              :menu-position="'left'"
                              class="ml-1"
                              hide-label
                              icon
                              menu-offset-x
                              small
                              @printer-selected="printer => printTableOrder(cart, printer)"
                            />

                            <external-receipt-toggle-button
                              v-if="externalReceipt && !issueReceipt"
                              :cart-id="viewData.Id"
                              :selected="!!cart.ExternalReceipt"
                              :table-id="table.Id"
                              :timestamp="cart.Timestamp"
                              class="ml-1"
                              icon
                              small
                            />

                            <manage-fiscal-documents-button
                              v-if="userCanViewFiscalDoc && issueReceipt && appHasFnbDocEnabled && !orderId"
                              :can-issue-document="cart.CanGenerateReceipt"
                              :order="cart"
                              :receipts="viewData.OrderReceipts"
                              :table="table"
                              :tooltip-open-delay="1000"
                              class="ml-1"
                              hide-label
                              icon
                              menu-offset-y
                              small
                              small-icon
                              small-size="28"
                              tooltip-position="bottom"
                              @issue-document="data => $bus.$emit('fiscal-document:create', {TableId: table.Id, CartId: viewData.Id, Timestamp: cart.Timestamp}, data.DocType)"
                            />
                          </template>
                        </template>

                        <template v-if="selectable">
                          <v-spacer />

                          <div class="d-inline-block">
                            <v-checkbox
                              :indeterminate="someOrderMenuItemsSelected(cart) && !allOrderMenuItemsSelected(cart)"
                              :input-value="allOrderMenuItemsSelected(cart)"
                              :value="someOrderMenuItemsSelected(cart)"
                              class="ma-1"
                              color="grey darken-2"
                              hide-details
                              @change="toggleOrderMenuItems(cart)"
                            />
                          </div>
                        </template>
                      </v-col>
                    </v-row>
                  </v-container>
                </div>
              </v-col>

              <!-- CART ITEMS -->
              <v-col cols="12">
                <v-container
                  class="mb-4 pa-0"
                  fluid
                >
                  <v-row dense>
                    <!-- ITEMS -->
                    <v-col cols="12">
                      <template v-for="tableSeat in getCartItems(cart, false, selectable, filterItemsByField, tab)">
                        <v-subheader
                          :key="`table-seat-${tableSeat.Seat}`"
                          :class="[`${tableStaffColor(cart.StaffId)}`, isDarkMode ? 'darken-3' : 'lighten-5']"
                          class="font-weight-bold pl-1 rounded"
                          style="height: 26px;"
                        >
                          {{ tableSeat.Name }}
                        </v-subheader>

                        <template v-for="category in tableSeat.Items">
                          <v-subheader
                            :key="`cart-${cart.Timestamp}-table-seat-${tableSeat.Seat}-category-${category.CategoryId}`"
                            class="grey--text text--darken-2 text-subtitle-1 font-weight-bold pl-0"
                            style="height: 36px;"
                          >
                            {{ getTranslatedField(getCategory(category.CategoryId), 'Name') || category.CategoryName }}
                          </v-subheader>

                          <v-list
                            :key="`cart-${cart.Timestamp}-table-seat-${tableSeat.Seat}-category-${category.CategoryId}-list`"
                            class="pa-0"
                            dense
                            three-line
                          >
                            <template v-for="(menu) in category.Items">
                              <v-list-item
                                :key="`cart-${cart.Timestamp}-table-seat-${tableSeat.Seat}-category-${category.CategoryId}-item-${menu.RowId}-hash-${menu.HashId}`"
                                :class="isDarkMode ? 'grey darken-4' : 'grey lighten-4'"
                                :disabled="menu.Loading"
                                ripple
                                v-on="selectable ? {click: () => toggleMenuItem(menu)} : editableItemUnits && menu.UnitId > 1 && menu.PaymentQuantity > 0 ? {click: () => showUnitQuantityDialog(menu, cart)} : {}"
                              >
                                <v-list-item-action
                                  v-if="selectable"
                                  class="my-auto"
                                >
                                  <v-checkbox
                                    :ripple="false"
                                    :value="isMenuItemSelected(menu)"
                                    hide-details
                                    readonly
                                    style="pointer-events: none;"
                                  />
                                </v-list-item-action>

                                <v-list-item-content>
                                  <span
                                    :style="`background-color: #${getCategoryColor(category.CategoryId) || '333333'};`"
                                    style="position: absolute; width:5px; height: 100%; margin-left: -16px; padding: 0; margin-right: 16px; top: 1px;"
                                  />

                                  <v-list-item-title class="text-subtitle-1 font-weight-regular">
                                    <template v-if="menu.MenuCustomPrice && menu.UnitId > 1">
                                      <v-icon
                                        v-if="editableItemUnits && menu.PaymentQuantity > 0 && cartItemsCanChangeUnitQuantity(cart.Timestamp, viewData.OrderReceipts)"
                                        class="mr-2"
                                      >
                                        mdi-keyboard-outline
                                      </v-icon>
                                      <strong class="orange--text">{{ formatUnitQuantity($i18n.locale, menu.UnitQuantity, getMeasurementUnitById(menu.UnitId)) }}</strong>
                                    </template>
                                    <template v-else>
                                      <strong class="orange--text">
                                        {{ getMenuQuantity(menu) }} x
                                      </strong>
                                    </template>
                                    {{ getTranslatedField(menu, 'Name') }}
                                  </v-list-item-title>

                                  <v-list-item-subtitle class="grey--text">
                                    <template v-if="menu.SpecialStatus && menu.IsSpecial && !menu.HasCustomPrice">
                                      <span class="old-price">
                                        {{ menu.MenuPrice | currency }}
                                      </span>
                                      {{ menu.SpecialPrice | currency }}
                                    </template>

                                    <template v-else>
                                      <template v-if="menu.MenuCustomPrice && menu.UnitId > 1">
                                        {{ menu.MenuPrice | currency }}
                                        <span class="grey--text text-caption">/ {{ getMeasurementUnitById(menu.UnitId).BaseUnit }}</span>
                                      </template>
                                      <template v-else>
                                        <span
                                          v-if="menuItemHasCoupon(menu)"
                                          class="old-price"
                                        >
                                          {{ parseFloat(menu.Price) | currency }}
                                        </span>
                                        {{ menuItemHasCoupon(menu) ? parseFloat(menu.PriceFmt) - (parseFloat(menu.Coupon.Amount) / getMenuQuantity(menu)) : menu.PriceFmt | currency }}
                                      </template>
                                    </template>
                                  </v-list-item-subtitle>

                                  <v-list-item-subtitle class="pb-1 text-caption grey--text">
                                    {{ getMenuSelectedOptionsString(menu) }}&nbsp;
                                  </v-list-item-subtitle>
                                </v-list-item-content>

                                <v-list-item-action>
                                  <v-list-item-action-text class="text-subtitle-1 grey--text">
                                    <template v-if="menu.SpecialStatus && menu.IsSpecial && !menu.HasCustomPrice">
                                      <span class="old-price">
                                        {{ parseFloat(parseFloat(menu.MenuPrice) * getMenuQuantity(menu)) | currency }}
                                      </span>
                                      {{ parseFloat(parseFloat(menu.SpecialPrice) * getMenuQuantity(menu)) | currency }}
                                    </template>

                                    <template v-else>
                                      <span
                                        v-if="menuItemHasCoupon(menu)"
                                        class="old-price"
                                      >
                                        {{ parseFloat(parseFloat(menu.Price) * getMenuQuantity(menu)) | currency }}
                                      </span>
                                      {{ menuItemPriceTotal(menu, menuItemHasCoupon(menu), false) | currency }}
                                    </template>
                                  </v-list-item-action-text>

                                  <v-list-item-action-text>
                                    <v-btn
                                      v-if="menu.Custom && parseInt(menu.Custom.Order)"
                                      class="d-inline-block ml-1 px-1"
                                      color="pink lighten-1"
                                      ripple
                                      small
                                      style="min-width: 0px;"
                                      text
                                    >
                                      <v-icon>
                                        low_priority
                                      </v-icon>

                                      <span style="position: relative; top: -4px;">{{ menu.Custom.Order }}</span>
                                    </v-btn>

                                    <v-btn
                                      v-if="menu.Custom && parseInt(menu.Custom.Seat)"
                                      class="d-inline-block ml-1 px-1"
                                      color="brown lighten-1"
                                      small
                                      style="min-width: 0px;"
                                      text
                                    >
                                      <v-icon>
                                        event_seat
                                      </v-icon>

                                      <span style="position: relative; top: -4px;">{{ menu.Custom.Seat }}</span>
                                    </v-btn>

                                    <v-btn
                                      v-if="menuItemHasCoupon(menu)"
                                      class="d-inline-block ml-1 px-1"
                                      color="purple"
                                      small
                                      style="min-width: 0px;"
                                      text
                                    >
                                      <v-icon>
                                        redeem
                                      </v-icon>
                                    </v-btn>

                                    <print-marketing-button
                                      v-if="printMarketing && menu.MarketingPrintTemplateId && posUser?.Settings?.AllUsers?.Permissions?.CanPrintMarketing"
                                      class="ml-1"
                                      hide-label
                                      icon
                                      menu-offset-x
                                      menu-position="left"
                                      small
                                      @printer-selected="printer => printMenuMarketing(menu.Id, menu.MarketingPrintTemplateId, printer)"
                                    />
                                  </v-list-item-action-text>
                                </v-list-item-action>
                              </v-list-item>

                              <v-divider
                                v-if="!cartItemHasExtraCharges(cart, menu)"
                                :key="`cart-${cart.Timestamp}-table-seat-${tableSeat.Seat}-category-${category.CategoryId}-item-divider-${menu.RowId}-hash-${menu.HashId}`"
                                :class="cartItemHasExtraCharges(cart, menu) ? 'mb-0' : 'mb-1'"
                              />

                              <!-- Menu Item Extra Charges -->
                              <template v-for="(charge, chargeIdx) in getMenuItemExtraCharges(menu)">
                                <v-divider
                                  v-if="chargeIdx > 0"
                                  :key="`cart-${cart.Timestamp}-table-seat-${tableSeat.Seat}-category-${category.CategoryId}-item-${menu.RowId}-hash-${menu.HashId}-charge-${charge.Id}-divider`"
                                />

                                <v-list-item
                                  :key="`cart-${cart.Timestamp}-table-seat-${tableSeat.Seat}-category-${category.CategoryId}-item-${menu.RowId}-hash-${menu.HashId}-charge-${charge.Id}`"
                                  :class="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                                  :style="selectable ? 'padding-left: 72px;' : ''"
                                  style="min-height: 30px !important; height: 30px;"
                                >
                                  <v-list-item-content class="py-0">
                                    <span
                                      :style="`background-color: #${getCategoryColor(category.CategoryId) || '333333'};`"
                                      style="position: absolute; width:5px; height: 100%; margin-left: -16px; padding: 0; margin-right: 16px; top: 0px;"
                                    />

                                    <v-list-item-title>
                                      <span class="orange--text">{{ charge.Quantity }} x </span>
                                      <span class="grey--text text--darken-1">
                                        {{ charge.Title }}
                                      </span>
                                    </v-list-item-title>
                                  </v-list-item-content>

                                  <v-list-item-action-text
                                    class="text-right"
                                    style="min-width: 50px;"
                                  >
                                    <span class="grey--text text--darken-1">
                                      +{{ charge.Amount | currency }}
                                    </span>
                                  </v-list-item-action-text>
                                </v-list-item>
                              </template>

                              <v-divider
                                v-if="cartItemHasExtraCharges(cart, menu)"
                                :key="`cart-${cart.Timestamp}-table-seat-${tableSeat.Seat}-category-${category.CategoryId}-item-charge-divider-${menu.RowId}-hash-${menu.HashId}`"
                                class="mb-1"
                              />
                            </template>
                          </v-list>
                        </template>
                      </template>
                    </v-col>

                    <!-- SUBTOTALS -->
                    <v-col
                      v-if="subTotals"
                      class="grey--text text--darken-1"
                      cols="12"
                    >
                      <template v-if="cartHasCoupons(cart)">
                        <div class="pl-1 pr-2 pt-1 pb-1">
                          <strong>
                            <span class="grey--text text--darken-1">
                              {{ $t('Common.Misc.Subtotal').toLocaleUpperCase($i18n.locale) }}
                            </span>
                            <span class="grey--text text--darken-1 float-right pr-2">
                              {{ cartOrderPriceTotal(cart, false) | currency }}
                            </span>
                          </strong>
                        </div>

                        <template v-for="(total, index) in cartCoupons(cart)">
                          <div
                            :key="index"
                            class="pl-1 pr-2"
                          >
                            <span class="grey--text text--darken-1">
                              {{ total.Title }}
                            </span>
                            <span class="grey--text text--darken-1 float-right pr-2">
                              -{{ total.Amount | currency }}
                            </span>
                          </div>
                        </template>
                      </template>

                      <div class="pl-1 pr-2 pt-1 pb-1">
                        <strong>
                          <span class="grey--text text--darken-1">
                            {{ cartHasCoupons(cart) ? $t('Common.Misc.Total').toLocaleUpperCase($i18n.locale) : $t('Common.Misc.Subtotal').toLocaleUpperCase($i18n.locale) }}
                          </span>
                          <span class="grey--text text--darken-1 float-right pr-2">
                            {{ cartOrderPriceTotal(cart, true) | currency }}
                          </span>
                        </strong>
                      </div>
                      <v-divider />
                    </v-col>
                  </v-row>
                </v-container>
              </v-col>
            </v-row>
          </v-container>
        </template>
      </v-col>

      <!-- TOTAL -->
      <v-col
        v-if="totals"
        class="grey--text text--darken-1 white"
        cols="12"
      >
        <div class="pl-1 pr-2 pt-1 pb-1">
          <strong>
            <span class="grey--text text--darken-1">
              {{ $t('Common.Misc.Total').toLocaleUpperCase($i18n.locale) }}
            </span>
            <span class="grey--text text--darken-1 float-right pr-2">
              {{ cartsPriceTotal() | currency }}
            </span>
          </strong>
        </div>
        <v-divider />
      </v-col>

      <num-pad-dialog
        v-if="selectedItem"
        :max-num-value="selectedItem.SubtractStock ? maxQuantity : undefined"
        :min-num-value="selectedItem.MinimumQuantity ? parseInt(selectedItem.MinimumQuantity) : undefined"
        :suffix="selectedItemRateUnit"
        :use-decimals="false"
        :visible.sync="unitQuantityDialogVisible"
        number
        @save="onSetUnitQuantity"
      />
    </v-row>
  </v-container>
</template>

<script>
import Auth from '@/mixins/auth'
import AppData from '@/mixins/appdata'
import Translatable from '@/mixins/translatable'
import TableStatus from '@/mixins/tables/tableStatus'
import PosCatalog from '@/mixins/pos/posCatalog'
import vModel from '@/mixins/vModel'
import CompletedData from '@/mixins/tables/completedData'
import TableCommon from '@/mixins/tables/tableCommon'
import Printer from '@/lib/print/printer.js'
import PrintButton from '@/components/common/print/PrintButton.vue'
import PrintPreparationButton from '@/components/common/print/PrintPreparationButton.vue'
import {formatUnitQuantity} from '@/lib/currency/currency'
import ExternalReceiptToggleButton from '@/components/common/print/ExternalReceiptToggleButton.vue'
import NumPadDialog from '@/components/common/NumPadDialog.vue'
import FiscalDocTypeEnum from '@/api/Enums/FiscalDocTypeEnum'
import ManageFiscalDocumentsButton from '@/components/common/receipt/ManageFiscalDocumentsButton.vue'
import PrintNotifyTypeEnum from '@/api/Enums/PrintNotifyTypeEnum.js'
import {arrayUniqueByObjectKey, clone} from '@/lib/helper/helper.js'
import PrintMarketingButton from '@/components/common/print/PrintMarketingButton.vue'

export default {
  name      : 'TableCompletedOrdersList',
  components: {
    PrintMarketingButton,
    ManageFiscalDocumentsButton,
    NumPadDialog,
    ExternalReceiptToggleButton,
    PrintPreparationButton,
    PrintButton
  },
  directives: {},
  mixins    : [Auth, AppData, vModel, Translatable, TableStatus, TableCommon, CompletedData, PosCatalog],
  props     : {
    viewData: {
      type   : Object,
      default: undefined
    },
    table: {
      type   : Object,
      default: undefined
    },
    canceled: {
      type   : Boolean,
      default: false
    },
    unpaid: {
      type   : Boolean,
      default: false
    },
    paid: {
      type   : Boolean,
      default: false
    },
    selectable: {
      type   : Boolean,
      default: false
    },
    editableItemUnits: {
      type   : Boolean,
      default: false
    },
    totals: {
      type   : Boolean,
      default: false
    },
    subTotals: {
      type   : Boolean,
      default: true
    },
    print: {
      type   : Boolean,
      default: false
    },
    externalReceipt: {
      type   : Boolean,
      default: true
    },
    issueReceipt: {
      type   : Boolean,
      default: true
    },
    printOrder: {
      type   : Boolean,
      default: true
    },
    printMarketing: {
      type   : Boolean,
      default: false
    },
    printOrderPreparation: {
      type   : Boolean,
      default: true
    },
    orderId: {
      type   : [Number, String],
      default: null
    },
    selectedTotal: {
      type   : Number,
      default: 0
    },
    tab: {
      type   : String,
      default: ''
    }
  },
  dataStore: {},
  data() {
    return {
      selectedItem             : null,
      unitQuantityDialogVisible: false
    }
  },
  computed: {
    FiscalDocTypeEnum() {
      return FiscalDocTypeEnum
    },
    selectedItemRateUnit() {
      return this.getMeasurementUnitById(this.selectedItem?.UnitId || 2)?.RateUnit
    },

    maxQuantity() {
      if (!this.selectedItem) return 0

      const menuItemStockQuantity = parseInt(this.selectedItem?.StockQuantity)
      const menuItemUnitQuantity = parseInt(this.selectedItem?.UnitQuantity)
      const menuItemQuantity = parseInt(this.selectedItem?.Quantity)

      const availableStockQuantity = menuItemStockQuantity
      const menuQuantity = this.userCanSetUnitQuantity ? menuItemUnitQuantity : menuItemQuantity

      return menuQuantity + availableStockQuantity
    },

    userCanSetUnitQuantity() {
      return this.selectedItem?.MenuCustomPrice && this.selectedItem?.UnitId > 1
    },

    ordersToday() {
      const orders = this.$DataStore?.ordersDataToday?.items || []
      return orders.filter(order => parseInt(order.StatusId) > 0)
    },

    isSelectedOrderToday() {
      return !!this.ordersToday.find(order => parseInt(order.Id) === parseInt(this.orderId))
    },

    filterItemsByField() {
      if (this.unpaid) return 'PaymentQuantity'
      if (this.canceled) return 'CancelQuantity'

      if (this.tab === 'tab-reorder') return 'PaymentQuantity'

      return null
    },

    selectedItemsTotal: {
      get() {
        return this.selectedTotal || 0
      },
      set(val) {
        this.$emit('update:selected-total', val)
      }
    },

    completedDataFiltered() {
      return this.completedData
        .filter(cart => this.orderHasMenuItems(cart))
        .filter(cart => this.paid ? cart.Paid : (this.unpaid ? !cart.Paid : true))
    },

    hasOrders() {
      if (this.canceled) return !!this.viewData?.CanceledCount
      if (this.unpaid) return !!this.viewData?.UnPaidCount
      if (this.paid) return !!this.viewData?.PaidCount
      return !!this.viewData?.OrdersCount
    }
  },
  watch: {
    vModel(items) {
      this.selectedItemsTotal = items?.reduce((value, menuItem) => value + this.menuItemPriceTotal(menuItem, true, true), 0) || 0
    }
  },
  beforeCreate() {
  },
  created() {
    this.$bus.$on('table-order-select-all-carts-items', this.selectAllCartOrderMenuItems)
  },
  beforeMount() {
  },
  mounted() {
  },
  beforeUpdate() {
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off('table-order-select-all-carts-items', this.selectAllCartOrderMenuItems)
  },
  destroyed() {
  },
  methods: {
    formatUnitQuantity,

    /**
     *
     * @param item {Object}
     * @param cart {Object}
     */
    showUnitQuantityDialog(item, cart) {
      if (!item || !cart || item?.UnitId === 1 || !this.cartItemsCanChangeUnitQuantity(cart.Timestamp, this.viewData.OrderReceipts)) return

      this.selectedItem = JSON.parse(JSON.stringify(item))
      this.selectedItem.Cart = JSON.parse(JSON.stringify(cart))
      this.unitQuantityDialogVisible = true
    },

    /**
     *
     * @param val {Number}
     */
    onSetUnitQuantity(val) {
      if (!val || !this.selectedItem) return

      window.callAS(window.SocketCommand.Table.Cart.UpdateUnitItem, {
        CartId      : this.table.CartId,
        RowId       : this.selectedItem.RowId,
        TableId     : this.table.Id,
        Timestamp   : this.selectedItem.Cart.Timestamp,
        UnitQuantity: val
      })

      this.selectedItem = null
    },

    /**
     *
     * @param cart {Object}
     */
    cartHasPreparationPrintsMissing(cart) {
      const prints = Array.isArray(cart?.Prints) ? cart?.Prints : []
      return prints.some(print => print.Type === PrintNotifyTypeEnum.PREPARATION && !print.ReceivedAt)
    },

    /**
     *
     * @param cart {Object}
     */
    cartHasReceipt(cart) {
      return !!this.cartReceiptByCart(cart)
    },

    /**
     *
     * @param cart {Object}
     */
    cartReceiptByCart(cart) {
      const receipts = Array.isArray(this.viewData?.Receipts) ? this.viewData?.Receipts : this.viewData?.Receipts ? [this.viewData?.Receipts] : null
      return receipts?.find(receipt => receipt.Timestamp === cart.Timestamp)
    },

    /**
     *
     * @param cart {Object}
     */
    orderReceiptByCart(cart) {
      const receipt = this.cartReceiptByCart(cart) || null
      const orderReceipt = this.viewData?.OrderReceipts?.find(o => o.OrderId === receipt?.OrderId)

      return orderReceipt || null
    },

    /**
     *
     * @param item {Object}
     */
    getMenuQuantity(item) {
      if (this.unpaid || (this.unpaid && this.selectable)) return parseInt(item.PaymentQuantity)
      if (this.canceled || (this.canceled && this.selectable)) return parseInt(item.CancelQuantity)

      return parseInt(item.Quantity)
    },

    /**
     *
     * @param item {Object}
     */
    isMenuItemSelected(item) {
      return !!this.vModel?.find(menu => menu.HashId === item.HashId)
    },

    /**
     *
     * @param item {Object}
     */
    toggleMenuItem(item) {
      if (!this.vModel) return

      const index = this.vModel?.findIndex(menu => menu.HashId === item.HashId)

      if (index > -1) {
        this.vModel.splice(index, 1)
      } else {
        this.vModel.push(item)
      }
    },

    /**
     *
     * @param cart {Object}
     */
    toggleOrderMenuItems(cart) {
      if (!this.vModel) return

      const seats = this.getCartItems(cart, false, this.selectable, this.filterItemsByField, this.tab)
      const items = []
      seats.forEach(seat => {
        seat.Items.forEach(category => {
          category.Items.forEach(menuItem => {
            items.push(menuItem)
          })
        })
      })

      const model = clone(this.vModel)
      const thisCartSelectedItems = model.filter(item => items.find(menu => menu.HashId === item.HashId))
      const otherCartSelectedItems = model.filter(item => !items.find(menu => menu.HashId === item.HashId))
      let tmpModel = thisCartSelectedItems

      if (tmpModel.length) {
        tmpModel = []
      } else {
        items.forEach(menuItem => {
          tmpModel.push(menuItem)
        })
      }

      this.vModel = arrayUniqueByObjectKey([...otherCartSelectedItems, ...tmpModel], 'HashId')

      this.$forceUpdate()
    },

    selectAllCartOrderMenuItems() {
      if (this.tab !== 'tab-unpaid') return

      const tmpModel = []

      this.completedDataFiltered.forEach(cart => {
        const seats = this.getCartItems(cart, false, this.selectable, this.filterItemsByField, this.tab)

        seats.forEach(seat => {
          seat.Items.forEach(category => {
            category.Items.forEach(menuItem => {
              const index = tmpModel.findIndex(menu => menu.HashId === menuItem.HashId)
              if (index > -1) {
                tmpModel.splice(index, 1)
              } else {
                tmpModel.push(menuItem)
              }
            })
          })
        })
      })

      this.vModel = tmpModel
    },

    /**
     *
     * @param cart {Object}
     */
    allOrderMenuItemsSelected(cart) {
      const items = this.getOrderMenuItems(cart)

      return items.every(menuItem => !!this.vModel && this.vModel.find(menu => menu.HashId === menuItem.HashId))
    },

    /**
     *
     * @param cart {Object}
     */
    someOrderMenuItemsSelected(cart) {
      const items = this.getOrderMenuItems(cart)

      return items.some(menuItem => !!this.vModel && this.vModel.find(menu => menu.HashId === menuItem.HashId))
    },

    /**
     *
     * @param cart {Object}
     */
    getOrderMenuItems(cart) {
      const items = []
      const seats = this.getCartItems(cart, false, this.selectable, this.filterItemsByField, this.tab)

      seats.forEach(seat => {
        seat.Items.forEach(category => {
          category.Items.forEach(menuItem => {
            items.push(menuItem)
          })
        })
      })

      return items
    },

    /**
     *
     * @param cart {Object}
     */
    orderHasMenuItems(cart) {
      return !!cart?.Items?.filter(item => this.canceled ? item.CancelQuantity : item.Quantity)?.length
    },

    /**
     *
     * @param cart {Object}
     * @param printer {Object}
     */
    printTableCanceled(cart, printer) {
      if (!cart || !printer) return
      const items = cart.Items.map(item => {
        return {
          RowId   : item.RowId,
          Quantity: item.CancelQuantity
        }
      })

      if (this.userIsWaiter) {
        window.callAS(window.SocketCommand.Table.SendCanceledForPrint, {
          cart_id: this.viewData.Id,
          items  : items
        })
      } else {
        Printer.printTableCanceled(this.viewData.Id, items, printer)
      }
    },

    /**
     *
     * @param cart {Object}
     * @param printer {Object}
     */
    printTableOrder(cart, printer) {
      if (!cart || !printer) return

      if (this.userIsWaiter) {
        window.callAS(window.SocketCommand.Table.SendOrderForPrint, {
          cart_id  : this.viewData.Id,
          timestamp: cart.Timestamp
        })
      } else {
        Printer.printTableOrder(this.viewData.Id, cart.Timestamp, printer)
      }
    },

    /**
     *
     * @param menuId {Number}
     * @param templateId {Number}
     * @param printer {Object}
     */
    printMenuMarketing(menuId, templateId, printer) {
      if (!menuId || !printer) return

      if (this.userIsWaiter) {
        window.callAS(window.SocketCommand.Table.SendOrderMarketingForPrint, {menu_id: menuId})
      } else {
        Printer.printMenuMarketing(menuId, templateId, printer)
      }
    },

    /**
     *
     * @param cart {Object}
     * @param printerGroup {Object}
     */
    printTableOrderPreparation(cart, printerGroup) {
      if (!cart || !printerGroup) return

      if (this.userIsWaiter) {
        window.callAS(window.SocketCommand.Table.SendOrderPreparationForPrint, {
          cart_id  : this.viewData.Id,
          timestamp: cart.Timestamp
        })
      } else {
        Printer.printTableOrderPreparation(this.viewData.Id, printerGroup.Id, cart.Timestamp)
      }
    }
  }
}
</script>

<style scoped>

</style>
